import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, MapPin, CheckCircle2, Clock, Plus, Trash2, RefreshCw, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function Progress({ tasks }) {
  const total = tasks.total || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <div className="mt-2">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-[#eee]">
        <div className="bg-[#2EC4B6]" style={{ width: `${pct(tasks.done)}%` }} />
        <div className="bg-[#FFC43D]" style={{ width: `${pct(tasks.in_progress)}%` }} />
        <div className="bg-[#d9d9d5]" style={{ width: `${pct(tasks.todo)}%` }} />
      </div>
      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[var(--text-2)]">
        <span>{tasks.done} done</span><span>{tasks.in_progress} in progress</span><span>{tasks.todo} to do</span>
      </div>
    </div>
  );
}

export default function TeamOverview() {
  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [newTeam, setNewTeam] = useState("");

  const loadOverview = useCallback(async () => {
    try { const r = await api.get("/team/overview"); setData(r.data); } catch (e) {}
  }, []);
  const loadTeams = useCallback(async () => {
    try { const r = await api.get("/teams"); setTeams(r.data); } catch (e) {}
  }, []);

  useEffect(() => {
    loadOverview(); loadTeams();
    const id = setInterval(loadOverview, 5000);
    return () => clearInterval(id);
  }, [loadOverview, loadTeams]);

  const addTeam = async () => {
    const name = newTeam.trim();
    if (!name) return;
    try { await api.post("/teams", { name }); setNewTeam(""); loadTeams(); loadOverview(); toast.success("Team created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTeam = async (id) => { try { await api.delete(`/teams/${id}`); loadTeams(); loadOverview(); } catch (e) {} };
  const assign = async (memberId, teamId) => {
    try { await api.patch(`/crew/${memberId}`, { team_id: teamId || null }); loadOverview(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleLead = async (m) => {
    try { await api.patch(`/crew/${m.id}`, { team_lead: !m.team_lead }); loadOverview(); toast.success(m.team_lead ? "Team leader removed" : "Team leader set"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const s = data?.summary;
  const stats = [
    { label: "Crew", value: s?.crew ?? 0 },
    { label: "Completed", value: s?.completed ?? 0 },
    { label: "Pending", value: s?.pending ?? 0 },
    { label: "Check-ins today", value: s?.checkins_today ?? 0 },
  ];

  return (
    <div data-testid="team-overview" className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><Users className="w-4 h-4" /> Team overview</p>
        <span className="text-[11px] text-[var(--text-2)] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> live · updates every 5s</span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((st) => (
          <div key={st.label} data-testid={`overview-stat-${st.label.replace(/\s+/g, "-").toLowerCase()}`} className="border border-[var(--line)] bg-white p-4">
            <p className="text-3xl font-serif leading-none">{st.value}</p>
            <p className="text-xs text-[var(--text-2)] mt-1">{st.label}</p>
          </div>
        ))}
      </div>

      {/* Manage teams */}
      <div className="border border-[var(--line)] bg-white p-4">
        <p className="label-mono text-[var(--text-2)] mb-3">Teams (optional grouping)</p>
        <div className="flex flex-wrap items-center gap-2">
          {teams.map((t) => (
            <span key={t.id} data-testid={`team-chip-${t.id}`} className="inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full bg-[#f4f4f2] border border-[var(--line)] text-sm">
              {t.name}
              <button data-testid={`team-del-${t.id}`} onClick={() => delTeam(t.id)} className="text-[var(--text-2)] hover:text-[var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
            </span>
          ))}
          <input data-testid="team-name-input" value={newTeam} onChange={(e) => setNewTeam(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addTeam(); }} placeholder="New team name…" className="h-8 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)] w-44" />
          <button data-testid="team-add" onClick={addTeam} className="h-8 px-3 text-sm border border-[var(--line)] hover:border-[var(--accent)] transition-colors flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add team</button>
        </div>
        <p className="text-xs text-[var(--text-2)] mt-2">No teams? Everyone shows under “My Crew”. Create teams to group crew, then assign each member below.</p>
      </div>

      {/* Groups */}
      {!data ? (
        <p className="text-sm text-[var(--text-2)]">Loading…</p>
      ) : (
        data.groups.map((g) => (
          <div key={g.id || "none"} data-testid={`overview-group-${g.id || "unassigned"}`} className="border border-[var(--line)] bg-white">
            <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--line)]">
              <h3 className="font-serif text-lg">{g.name}</h3>
              <span className="label-mono text-[var(--text-2)]">{g.members.length} member{g.members.length === 1 ? "" : "s"}</span>
            </div>
            <div className="p-4 space-y-3">
              {g.members.length === 0 && <p className="text-xs text-[var(--text-2)]">No crew in this group.</p>}
              {g.members.map((m) => (
                <div key={m.id} data-testid={`overview-member-${m.id}`} className="border border-[var(--line)] bg-[#f9f9f7] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${m.active ? "bg-[#2EC4B6]" : "bg-[#FF6B6B]"}`} />
                        {m.name} {!m.active && <span className="text-xs text-[var(--danger)]">(inactive)</span>}
                        {m.team_lead && <span data-testid={`lead-badge-${m.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#946200] bg-[#FFC43D]/20 border border-[#FFC43D]/50 px-1.5 py-0.5 rounded-full"><Star className="w-2.5 h-2.5 fill-current" /> Lead</span>}
                      </p>
                      <p className="text-xs text-[var(--text-2)] font-mono truncate">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`overview-lead-${m.id}`} onClick={() => toggleLead(m)} disabled={!m.team_id && !m.team_lead} title={!m.team_id ? "Assign a team first" : ""} className={`inline-flex items-center gap-1 h-8 px-2 border text-xs transition-colors disabled:opacity-40 ${m.team_lead ? "border-[#FFC43D] text-[#946200] bg-[#FFC43D]/10" : "border-[var(--line)] text-[var(--text-2)] hover:border-[var(--accent)]"}`}>
                        <Star className={`w-3.5 h-3.5 ${m.team_lead ? "fill-current" : ""}`} /> {m.team_lead ? "Leader" : "Make lead"}
                      </button>
                      <select data-testid={`overview-assign-${m.id}`} value={m.team_id || ""} onChange={(e) => assign(m.id, e.target.value)} className="h-8 px-2 border border-[var(--line)] bg-white text-xs outline-none focus:border-[var(--accent)]">
                        <option value="">No team</option>
                        {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <Progress tasks={m.tasks} />

                  <div className="mt-3">
                    <p className="text-[11px] uppercase tracking-wider text-[var(--text-2)] mb-1.5">Recent check-ins</p>
                    {(m.recent_checkins || []).length === 0 ? (
                      <p className="text-xs text-[var(--text-2)]">None yet</p>
                    ) : (
                      <div className="space-y-1.5">
                        {m.recent_checkins.map((c) => (
                          <div key={c.id} className="flex items-center gap-2 text-xs text-[var(--text-2)]">
                            <MapPin className="w-3 h-3 text-[var(--accent)] shrink-0" />
                            <span className="truncate flex-1">{c.task_title || "On-site"}{c.note ? ` — ${c.note}` : ""}</span>
                            <span className="font-mono flex items-center gap-1 shrink-0"><Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                            {c.photo_url && <a data-testid={`checkin-photo-${c.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} target="_blank" rel="noreferrer" className="shrink-0"><img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt="proof" className="w-6 h-6 object-cover rounded border border-[var(--line)]" /></a>}
                            {c.maps_url && <a href={c.maps_url} target="_blank" rel="noreferrer" className="text-[var(--accent)] hover:underline shrink-0">map</a>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
